<div class="grants-container">
  <!-- Header -->
  <div class="section-header">
    <div class="header-content">
      <h2>Private Action Grants</h2>
      <p class="section-description">Grant access to private actions for specific organizations</p>
    </div>
    <button class="btn btn-primary" (click)="startCreate()" [disabled]="isCreating || isSaving">
      <i class="fas fa-plus"></i> Grant Action
    </button>
  </div>

  <!-- Messages -->
  <div class="message error-message" *ngIf="errorMessage">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
  </div>
  <div class="message success-message" *ngIf="successMessage">
    <i class="fas fa-check-circle"></i> {{ successMessage }}
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <i class="fas fa-spinner fa-spin"></i> Loading grants...
  </div>

  <!-- Filters -->
  <div class="filters-row" *ngIf="!isLoading">
    <div class="search-box">
      <i class="fas fa-search"></i>
      <input type="text" [(ngModel)]="searchQuery" placeholder="Search grants..." />
    </div>
    <div class="filter-group">
      <label>Status:</label>
      <select [(ngModel)]="statusFilter">
        <option value="all">All</option>
        <option value="Active">Active</option>
        <option value="Revoked">Revoked</option>
      </select>
    </div>
  </div>

  <!-- Create Form -->
  <div class="create-form card" *ngIf="isCreating">
    <h3>Grant Private Action</h3>
    <div class="form-grid">
      <div class="form-group" *ngIf="!organizationId">
        <label>Organization *</label>
        <select [(ngModel)]="createForm.organizationId">
          <option value="">Select organization...</option>
          <option *ngFor="let org of organizations" [value]="org.ID">{{ org.Name }}</option>
        </select>
      </div>
      <div class="form-group">
        <label>Private Action *</label>
        <select [(ngModel)]="createForm.izzyActionId">
          <option value="">Select action...</option>
          <option *ngFor="let action of availableActions" [value]="action.ID">{{ action.Name }}</option>
        </select>
        <small class="help-text" *ngIf="availableActions.length === 0 && createForm.organizationId">
          All private actions already granted to this organization
        </small>
      </div>
      <div class="form-group">
        <label>Expires At</label>
        <input type="date" [(ngModel)]="createForm.expiresAt" />
        <small class="help-text">Leave blank for no expiration</small>
      </div>
      <div class="form-group">
        <label>Status</label>
        <select [(ngModel)]="createForm.status">
          <option value="Active">Active</option>
          <option value="Revoked">Revoked</option>
        </select>
      </div>
      <div class="form-group full-width">
        <label>Notes</label>
        <textarea [(ngModel)]="createForm.notes" placeholder="Optional notes about this grant" rows="2"></textarea>
      </div>
    </div>
    <div class="form-actions">
      <button class="btn btn-secondary" (click)="cancelCreate()" [disabled]="isSaving">Cancel</button>
      <button class="btn btn-primary" (click)="saveCreate()" [disabled]="isSaving || !createForm.izzyActionId">
        <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
        {{ isSaving ? 'Saving...' : 'Grant Access' }}
      </button>
    </div>
  </div>

  <!-- Grants Table -->
  <div class="table-container card" *ngIf="!isLoading">
    <table class="data-table">
      <thead>
        <tr>
          <th>Action</th>
          <th *ngIf="!organizationId">Organization</th>
          <th>Granted</th>
          <th>Expires</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let gd of filteredGrants" [class.expired]="isExpired(gd.grant)" [class.editing]="gd.isEditing">
          <!-- View Mode -->
          <ng-container *ngIf="!gd.isEditing">
            <td class="action-cell">
              <div class="action-info">
                <i class="fas fa-bolt"></i>
                <span>{{ gd.action?.Name || 'Unknown Action' }}</span>
              </div>
            </td>
            <td *ngIf="!organizationId">{{ gd.organization?.Name || 'Unknown' }}</td>
            <td class="date-cell">{{ formatDate(gd.grant.GrantedAt) }}</td>
            <td class="date-cell">
              <span [class.expired-date]="isExpired(gd.grant)">
                {{ formatDate(gd.grant.ExpiresAt) }}
              </span>
              <span class="expired-badge" *ngIf="isExpired(gd.grant)">Expired</span>
            </td>
            <td>
              <span class="status-badge" [ngClass]="getStatusClass(gd.grant.Status)">
                {{ gd.grant.Status }}
              </span>
            </td>
            <td class="actions-cell">
              <button class="btn btn-icon" title="Edit" (click)="startEdit(gd)" [disabled]="isSaving">
                <i class="fas fa-edit"></i>
              </button>
              <button class="btn btn-icon btn-danger" title="Revoke" (click)="confirmRevoke(gd)" [disabled]="isSaving">
                <i class="fas fa-ban"></i>
              </button>
            </td>
          </ng-container>

          <!-- Edit Mode -->
          <ng-container *ngIf="gd.isEditing">
            <td class="action-cell">
              <div class="action-info">
                <i class="fas fa-bolt"></i>
                <span>{{ gd.action?.Name || 'Unknown Action' }}</span>
              </div>
            </td>
            <td *ngIf="!organizationId">{{ gd.organization?.Name || 'Unknown' }}</td>
            <td class="date-cell">{{ formatDate(gd.grant.GrantedAt) }}</td>
            <td>
              <input type="date" [(ngModel)]="gd.editForm.expiresAt" class="inline-input" />
            </td>
            <td>
              <select [(ngModel)]="gd.editForm.status" class="inline-select">
                <option value="Active">Active</option>
                <option value="Revoked">Revoked</option>
              </select>
            </td>
            <td class="actions-cell">
              <button class="btn btn-icon" title="Save" (click)="saveEdit(gd)" [disabled]="isSaving">
                <i class="fas fa-check"></i>
              </button>
              <button class="btn btn-icon" title="Cancel" (click)="cancelEdit(gd)" [disabled]="isSaving">
                <i class="fas fa-times"></i>
              </button>
            </td>
          </ng-container>
        </tr>
      </tbody>
    </table>

    <!-- Empty State -->
    <div class="empty-state" *ngIf="filteredGrants.length === 0 && !isCreating">
      <i class="fas fa-lock"></i>
      <p>No private action grants found</p>
      <button class="btn btn-primary" (click)="startCreate()">Grant First Action</button>
    </div>
  </div>

  <!-- Confirm Dialog -->
  <div class="modal-overlay" *ngIf="confirmDialogVisible" (click)="closeConfirmDialog()">
    <div class="modal-dialog" (click)="$event.stopPropagation()">
      <h3>{{ confirmDialogTitle }}</h3>
      <p>{{ confirmDialogMessage }}</p>
      <div class="modal-actions">
        <button class="btn btn-secondary" (click)="closeConfirmDialog()">Cancel</button>
        <button class="btn btn-danger" (click)="confirmDialogYes()" [disabled]="isSaving">
          <i class="fas fa-spinner fa-spin" *ngIf="isSaving"></i>
          Revoke
        </button>
      </div>
    </div>
  </div>
</div>
